'use client';

import { useCallback, useMemo } from 'react';

import type { ComboboxOptionType, ComboboxOptionsType } from '../types/combobox-root.types';
import { isGroup } from '../utils/is-group';

function getValueByOption(option: ComboboxOptionType): string {
  return typeof option === 'string' ? option : option.value;
}

/**
 * Custom hook that provides utility functions for working with combobox options
 *
 * This hook flattens the options structure (including options in groups)
 * and provides functions to interact with options and their values.
 *
 * @param params - Parameters for the hook
 * @param params.options - The complete list of options (can include groups)
 * @returns An object containing utility functions for working with combobox options
 */
export const useComboboxOptionUtils = ({ options }: { options: ComboboxOptionsType }) => {
  /*
   * Flatten the options structure once per change to `options`. Used as the
   * stable dependency below so the returned getter identities only change
   * when the underlying option set changes.
   */
  const flattenedOptions = useMemo(() => {
    const result: Array<ComboboxOptionType> = [];

    /**
     * Recursively collects all individual options into a flat array
     *
     * @param items - The options or groups to process
     */
    const flattenOptions = (items: ComboboxOptionsType) => {
      for (const item of items) {
        if (isGroup(item)) {
          /* For groups, process their nested options */
          flattenOptions(item.options);
        } else {
          /* For individual options, add them to the result array */
          result.push(item);
        }
      }
    };

    flattenOptions(options);
    return result;
  }, [options]);

  /**
   * Gets an option by its index in the flattened options array
   *
   * @param index - The index of the option to retrieve (or null)
   * @returns The option at the specified index, or undefined if index is null or out of bounds
   */
  const getOptionByIndex = useCallback(
    (index: number | null): ComboboxOptionType | undefined => {
      if (index === null || index < 0 || index >= flattenedOptions.length) {
        return undefined;
      }

      return flattenedOptions[index];
    },
    [flattenedOptions],
  );

  /**
   * Gets the index of an option in the flattened options array by its value
   *
   * @param value - The string value to find
   * @returns The index of the option with matching value if found, or -1 if not found
   */
  const getIndexByValue = useCallback(
    (value: string): number => {
      return flattenedOptions.findIndex((item) =>
        typeof item === 'string' ? item === value : item.value === value,
      );
    },
    [flattenedOptions],
  );

  return useMemo(
    () => ({
      getOptionByIndex,
      getValueByOption,
      getIndexByValue,
    }),
    [getOptionByIndex, getIndexByValue],
  );
};
